<template>
  <v-container fluid class="pb-8 pt-8">
    <div class="flex-container" v-if="!isLoading && !timeInStatusDataLoading">
      <div
        v-show="$vuetify.breakpoint.smAndUp && !showHierarchies"
        class="hierarchies-collapsed"
        v-if="definition.showHierarchyFilterPanel"
      >
        <v-btn
          fab
          elevation="2"
          x-small
          title="Show Advanced Filters"
          @click="showHierarchies = !showHierarchies"
          absolute
          color="primary"
          class="showHierarchies"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <v-navigation-drawer
        v-show="$vuetify.breakpoint.smAndUp"
        v-model="showHierarchies"
        absolute
        class="left-col"
        style="height: 100vh"
        v-if="definition.showHierarchyFilterPanel"
      >
        <v-btn
          fab
          elevation="2"
          x-small
          title="Hide Advanced Filters"
          @click="showHierarchies = !showHierarchies"
          absolute
          color="primary"
          class="showHierarchies"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-toolbar-title class="d-flex align-center pl-1 pt-5">
          <span class="ml-2">Advanced Filters</span>
        </v-toolbar-title>

        <HierarchySearch
          v-if="showHierarchies"
          :items="applicationListFiltered"
          :filters="nonHierarchyFilters"
          :preSelected="preSelectedHierarchyFilters"
          :preSelectedClassifiers="preSelectedClassifierFilters"
          :hierarchyTypes="hierarchyTypes"
          :classifierTypes="classifierTypes"
          @filterChanged="hryFilterChanged"
        ></HierarchySearch>
      </v-navigation-drawer>

      <v-container
        fluid
        :class="{
          'right-col': true,
          hierarchiesCollapsed: !showHierarchies && $vuetify.breakpoint.smAndUp,
          hierarchiesPinned: showHierarchies && $vuetify.breakpoint.lgAndUp,
        }"
      >
        <v-row
          ><v-col
            :cols="reportingStats.length > 1 ? 6 : 12"
            v-for="(r, ri) in reportingStats.filter((x) => x && hasResults)"
            :key="'r' + ri"
          >
            <v-row
              v-if="reportingStats.length > 1"
              @click="r.show = !r.show"
              style="cursor: pointer"
              ><v-col
                ><h3>
                  {{ r.title }}
                </h3></v-col
              ><v-col cols="1"
                ><v-icon>{{
                  r.show ? "mdi-chevron-up" : "mdi-chevron-down"
                }}</v-icon></v-col
              >
            </v-row>
            <v-row>
              <v-col cols="3" v-if="r.showPieChart && r.show">
                <div class="pieContainer" style="opacity: 0.7">
                  <DoughnutChart
                    v-if="r.pieChartData"
                    :chartData="r.pieChartData"
                    :height="150"
                    :centralLabel="`<h2>${totalItemCount}</h2>TOTAL`"
                  ></DoughnutChart>
                </div>
              </v-col>
              <v-col v-if="r.showCounts && r.show">
                <v-row class="statusCounts">
                  <v-col
                    v-for="(sc, sci) in r.data"
                    :key="sci"
                    @click="toggleStatusFilter(r, sc)"
                    :class="r.filter === sc.status ? 'active' : ''"
                  >
                    <div class="pr-2" style="opacity: 0.7">
                      <v-icon size="16" :color="sc.colour">circle</v-icon>
                    </div>
                    <div>
                      <h3>{{ sc.count }} ({{ Math.round(100 * sc.count / totalItemCount) }}%)</h3>
                      <div class="text-uppercase mt-1">{{ sc.status }}</div>
                    </div>
                  </v-col>
                </v-row>
              </v-col></v-row
            >
          </v-col>
        </v-row>
        <v-row
          dense
          v-if="
            hasResults &&
            (definition.showHierarchyFilters ||
              definition.showManagerFilter ||
              definition.showDateFilter)
          "
        >
          <v-col v-if="definition.showDocTypeFilter && definitionDocTypes.length !== 0">
            <v-select
              v-model="docTypeIncluded"
              :items="definitionDocTypes"
              item-text="docType"
              item-value="docType"
              return-object
              label="Document Types"
              multiple
              :menu-props="{
                closeOnContentClick: false,
                maxHeight: '400',
                'offset-y': true,
              }"
              dense
              hide-details
              outlined
              class="roundish"
            >
              <template v-slot:selection="{ item, index }">
                {{ getDocTypeFilterText(item, index) }}
              </template>
            </v-select>
          </v-col>
          <v-col v-if="definition.showReviewGroupsFilter">
            <v-autocomplete
              multiple
              v-model="reviewGroupsIncluded"
              label="Review Groups"
              :items="reviewGroupsSummary"
              outlined
              dense
              clearable
              class="roundish"
              :menu-props="{ offsetY: true }"
            >
            </v-autocomplete>
          </v-col>
          <v-col v-if="definition.showManagerFilter">
            <PeopleFilter
              v-model="peopleIncluded"
              roundish
              @clear="peopleIncluded.splice(0)"
            >
            </PeopleFilter>
          </v-col>
          <v-col v-if="definition.showDateFilter">
            <v-menu
              ref="menu"
              v-model="docCreatedDateRangeMenu"
              :close-on-content-click="false"
              :return-value.sync="docCreatedDateRange"
              transition="slide-y-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <!-- prepend-inner-icon="mdi-calendar" -->
                <v-text-field
                  v-model="docCreatedDateRangeText"
                  label="Created Between"
                  dense
                  hide-details
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  :append-icon="
                    docCreatedDateRangeText ? 'mdi-close' : ''
                  "
                  @click:append="clearDateFilter"
                  placeholder="e.g 01 Jan - 30 Jan"
                >
                </v-text-field>
              </template>
              <v-date-picker
                v-model="docCreatedDateRange"
                no-title
                scrollable
                range
              >
                <v-btn text @click="clearDateFilter"> Clear </v-btn>
                <v-btn text @click="docCreatedDateRangeMenu = false">
                  Cancel
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="doDateFilter">
                  OK
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
          <template v-if="definition.showHierarchyFilters">
            <v-col v-for="(h, hi) in hierarchies" :key="'h' + hi">
              <HierarchyTreePicker
                v-if="useTreePicker"
                v-model="h.treePickerValue"
                :label="h.label"
                :options="h.treePickerOptionsActive"
                placeholder="Select..."
                :hierarchyType="h"
                clearable
                dense
              >
              </HierarchyTreePicker>
              <v-autocomplete
                v-else
                dense
                outlined
                clearable
                class="roundish"
                auto-select-first
                :ref="'hcb' + hi"
                return-object
                :items="h.values"
                item-value="text"
                item-text="text"
                :label="h.label"
                v-model="h.hierarchy_text"
                @focus="focusHTID = hi"
                @keyup="searchText($event, h)"
                :filter="utils.comboFilterPicker"
                hide-details
                :append-icon="h.icon"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content style="max-width: 800px">
                    <v-list-item-title>
                      {{ item.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
                <template v-slot:prepend-item>
                  <div
                    v-if="h.showNewPrompt"
                    style="padding: 0px 0px 10px 10px; font-size: 14px"
                  >
                    <div>
                      Press <kbd>Enter</kbd> to pick the highlighted item
                    </div>
                  </div>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ h.not_in_list_message }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
          </template>
          <v-col v-if="definition.showTimeInStatusFilter">
            <TimeInStatusFilter
              v-model="timeInStatusIncluded"
              :items="applicationList"
              roundish
            >
            </TimeInStatusFilter>
          </v-col>
          <v-col cols="1" class="d-flex justify-end">
            <v-menu offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <v-btn
                  title="Customise Table"
                  large
                  v-on="on"
                  icon
                  class="btn-background ml-3"
                >
                  <v-icon>mdi-cog</v-icon>
                </v-btn>
              </template>
              <v-list dense class="pa-3">
                <v-list-item>
                  <v-list-item-title class="subtitle-1"
                    >Customise Table</v-list-item-title
                  >
                </v-list-item>
                <v-list-item
                  v-for="field in dtColumns.filter(
                    (c) => $loginState.canDemo || !c.demoOnly
                  )"
                  :key="field.value"
                >
                  <v-switch
                    class="v-input--reverse v-input--expand"
                    style="width: 200px"
                    dense
                    v-model="dtColumnsEnabled"
                    :label="field.text"
                    :title="field.text"
                    :value="field.value"
                  ></v-switch>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
        <v-row v-if="applicationListHryFiltered.length !== 0" class="pb-6">
          <v-col cols="12">
            <v-card>
              <v-card-text>
                <DocumentDataTable
                  :headers="dtColumnsFiltered"
                  :items="applicationListHryFiltered"
                  :totalItemCount="useIndex ? totalItemCount : undefined"
                  :page.sync="currentPage"
                  :items-per-page="itemsPerPage"
                  :sortColumn.sync="sortColumn"
                  :sortDesc.sync="sortDesc"
                  density="normal"
                  @openDocument="openDocument"
                >
                </DocumentDataTable>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <div
          v-if="hasResults"
          class="d-flex justify-space-between footer-actions primary-background"
        >
          <PageDescription
            :totalItems="totalItemCount"
            :pageSize="itemsPerPage"
            :currentPage="currentPage"
          />
          <Pagination
            :totalItems="totalItemCount"
            :pageSize="itemsPerPage"
            :currentPage="currentPage"
            @pageNavigation="currentPage = $event"
          />
          <div>
            <v-btn
              outlined
              color="primary"
              small
              class="mr-2"
              @click="exportToCSV"
              >Export</v-btn
            >
            <v-menu offset-y z-index="301">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="!$loginState.readOnly"
                  outlined
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ itemsPerPageText }}
                  <v-icon right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item
                  v-for="item in itemsPerPageOptions"
                  :key="item.value"
                  @click="itemsPerPage = item.value"
                >
                  <v-list-item-title>
                    {{ item.text }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-container>
    </div>
    <Loading :isVisible="isLoading || timeInStatusDataLoading" />
  </v-container>
</template>


<script>
import utils from "@/common/utils.js";
import moment from "moment";
import { mapState } from "vuex";
import DoughnutChart from "@/components/common/DoughnutChart";
import Pagination from "@/components/cPagination";
import PageDescription from "@/components/cPageDescription";
import HierarchyTreePicker from "@/components/cHierarchyTreePicker";
import PeopleFilter from "@/components/cPeopleFilter.vue";
import TimeInStatusFilter from "@/components/cTimeInStatusFilter.vue";
import DocumentDataTable from "@/components/cDocumentDataTable.vue";
import HierarchySearch from "@/components/cHierarchySearch";
import axios from "axios";

let timeInStatusData = new Map();

export default {
  name: "DocumentList",
  components: {
    DoughnutChart,
    Pagination,
    PageDescription,
    HierarchyTreePicker,
    PeopleFilter,
    TimeInStatusFilter,
    DocumentDataTable,
    HierarchySearch,
  },
  props: {
    definition: { type: Object },
  },
  data: function () {
    return {
      utils: utils,
      initialised: false,
      searchInProgress: false,
      hierarchies: [],
      showHierarchies: false,
      useTreePicker: false,
      reviewGroupsIncluded: [],
      peopleIncluded: [],
      docCreatedDateRange: [],
      docCreatedDateRangeMenu: false,
      timeInStatusIncluded: [],
      docTypeIncluded: [],
      hasResults: false,
      applicationList: [],
      totalItemCount: 0,
      applicationListFiltered: [],
      applicationListHryFiltered: [],
      preSelectedHierarchyFilters: [],
      preSelectedClassifierFilters: [],
      reportingStats: [],
      endIn30: "Ending in 30 days",
      dtColumns: [],
      dtColumnsEnabled: [],
      currentPage: 1,
      itemsPerPage: 10,
      itemsPerPageOptions: [
        { text: "10 Rows", value: 10 },
        { text: "20 Rows", value: 20 },
        { text: "30 Rows", value: 30 },
        { text: "50 Rows", value: 50 },
        { text: "All", value: -1 },
      ],
      sortColumn: "system_number",
      sortDesc: false,
      pieChartSettings: {
        measures: [
          {
            name: "StatusCounts",
            measureColumn: "count",
            sort: "",
          },
        ],
        activeMeasures: ["StatusCounts"],
        hideLegend: true,
        hideLabels: true,
      },
      questionnaireStatusColumns: [],
      //questionnaires: [],
      doc_id_opened: null,
      timeInStatusDataLoading: false,
    };
  },
  computed: {
    ...mapState({
      docsLoading: (state) => state.docs.loading,
      docsList: (state) => state.docs.docsList,
      docsListUpdated: (state) => state.docs.listUpdated,
      docStatusSummary: (state) => state.docs.docStatusSummary,
      docStatuses: (state) => state.docs.allDocStatuses,
      docTypeSummary: (state) => state.docs.docTypeSummary,
      reviewGroupsSummary: (state) => state.docs.reviewGroupsSummary,
      questionnaires: (state) => state.docs.questionnaires,
      hierarchyTypes: (state) => state.docs.hierarchyTypes,
      classifierTypes: (state) => state.docs.classifierTypes,
      hierarchiesLoading: (state) => state.hierarchies.loading,
      statuses: (state) => state.hierarchies.statuses,
      docTypes: (state) => state.hierarchies.docTypes,
      useIndex: (state) => state.docs.useIndex,
    }),
    docStatusIncluded() {
      const selectedStatus = this.reportingStats.find((r) => r.column === 'statusDisplayText' && r.filter)?.filter;
      if (selectedStatus) {
        return this.docStatusSummary.filter(s => s.status === selectedStatus);
      }

      const included = this.definition.statuses || [];
      const excluded = this.definition.statusesExcluded || [];
      if (!included.length && !excluded.length)
        return this.docStatusSummary.filter(s => s.show);
      else if (included.length)
        return this.docStatusSummary.filter(s => included.includes(s.status) && !excluded.includes(s.status));
      else
        return this.docStatusSummary.filter(s => !excluded.includes(s.status));
    },
    definitionDocTypes() {
      return  this.docTypeSummary.filter((s) => !this.definition.docTypes || this.definition.docTypes.length === 0 || this.definition.docTypes.some(dt => dt.toLowerCase() === s.docType.toLowerCase()));
    },
    lifecycleIdsIncluded() {
      if (!this.definition.lifecycles) return null;

      return this.docTypes
        .filter((s) => !this.definition.docTypes || this.definition.docTypes.length === 0 || this.definition.docTypes.some(dt => dt.toLowerCase() === s.tmpl_name.toLowerCase()))
        .reduce((p, c) => {
          return [...p, ...c.lifecycles?.filter((l) => !this.definition.lifecycles || this.definition.lifecycles.length === 0 || this.definition.lifecycles.includes(l.lifecycle_name)) ]
        }, [])
        .map((l) =>
         l.lifecycle_id);
    },
    dtColumnsFiltered() {
      return [
        ...this.dtColumns.filter(
          (c) =>
            this.dtColumnsEnabled.indexOf(c.value) >= 0 &&
            (this.$loginState.canDemo || !c.demoOnly)
        ),
        //{ text: "", value: "export_menu", sortable: false, align: "end" },
      ];
    },
    docCreatedDateRangeText: {
      get() {
        const range = this.docCreatedDateRange;
        if (!range.length) return "";
        if (range.length == 1) return moment(range[0]).format("DD MMM YYYY");

        return range
          .map((d) => {
            return moment(d).format("DD MMM");
          })
          .join(" - ");
      },
      set(newValue) {
        const range = newValue.split('-').map(x => x.trim()).filter(x => x);
        if (range.length > 0 && range[0].length >= 8)
          this.docCreatedDateRange[0] = moment(range[0], 'DD MMM YYYY').format("YYYY-MM-DD");
        else if (range.length > 0 && range[0].length >= 4)
          this.docCreatedDateRange[0] = moment(range[0], 'DD MMM').format("YYYY-MM-DD");
        else
          this.docCreatedDateRange.splice(0);

        if (range.length > 1 && range[1].length >= 8)
          this.docCreatedDateRange[1] = moment(range[1], 'DD MMM YYYY').format("YYYY-MM-DD");
        if (range.length > 1 && range[1].length >= 4)
          this.docCreatedDateRange[1] = moment(range[1], 'DD MMM').format("YYYY-MM-DD");
        else if (this.docCreatedDateRange.length !== 0)
          this.docCreatedDateRange[1] = moment().format("YYYY-MM-DD");
      }
    },
    nonHierarchyFilters() {
      return {
        filterText: (this.search || "").trim().toLowerCase(),
        docStatusIncluded: this.docStatusIncluded,
        docTypeIncluded: this.docTypeIncluded,
        docCreatedDateRange: this.docCreatedDateRange,
        lifecycleIdsIncluded: this.lifecycleIdsIncluded,
        reviewGroupsIncluded: this.reviewGroupsIncluded,
        peopleIncluded: this.peopleIncluded,
        timeInStatusIncluded: this.timeInStatusIncluded,
      };
    },
    filters() {
      const hierarchyFilters = this.useTreePicker
        ? this.hierarchies.map((h) => {
            return {
              ht_id: h.ht_id,
              selected: h.treePickerValue
                ? [{ id: `${h.treePickerValue}` }]
                : [],
            };
          })
        : this.hierarchies.map((h) => {
            return {
              ht_id: h.ht_id,
              selected: h.hierarchy_text
                ? [{ id: `${h.hierarchy_text.value}` }]
                : [],
            };
          });
      if (this.useIndex && this.preSelectedHierarchyFilters.length)
          hierarchyFilters.push(...this.preSelectedHierarchyFilters);
      const classifierFilters = [];
      if (this.useIndex && this.preSelectedClassifierFilters.length)
          classifierFilters.push(...this.preSelectedClassifierFilters);

      const fieldFilters = this.reportingStats.map((r => {
        if (r.column && r.filter) {
          if (r.column === 'statusDisplayText') {
            // already handled in docStatusIncluded
            return null;
          } else if (r.column === 'docQuestionnaireStatus') {
            return {
              attr: `qs_${r.id}`,
              val: [r.filter],
            };
          }
        }
      })).filter(f => f);

      let tisDataIncluded;
      if (this.useIndex && this.timeInStatusIncluded && this.timeInStatusIncluded.length) {
        tisDataIncluded = Array.from(timeInStatusData.values());
        this.timeInStatusIncluded.forEach(f => {
          tisDataIncluded = tisDataIncluded.filter(d => {
            const daysInStatus = Math.round(d[`tis_${f.status}`] / (24 * 60)) || 0;
            return daysInStatus >= f.min && daysInStatus <= f.max;
          })
        })
      }

      return {
        filterText: (this.search || "").trim().toLowerCase(),
        docStatusIncluded: this.docStatusIncluded,
        docTypeIncluded: this.docTypeIncluded,
        docCreatedDateRange: this.docCreatedDateRange,
        lifecycleIdsIncluded: this.lifecycleIdsIncluded,
        hierarchyFilters: hierarchyFilters,
        classifierFilters: classifierFilters,
        reviewGroupsIncluded: this.reviewGroupsIncluded,
        peopleIncluded: this.peopleIncluded,
        timeInStatusIncluded: this.timeInStatusIncluded,
        docIdsIncluded: tisDataIncluded ? tisDataIncluded.map(d => d.doc_id) : null,
        filters: fieldFilters,
      };
    },
    itemsPerPageText() {
      const option = this.itemsPerPageOptions.find(
        (o) => o.value == this.itemsPerPage
      );
      return option ? option.text : `$(itemsPerPage) Items`;
    },
    isLoading() {
      return this.docsLoading || this.hierarchiesLoading;
    },
    isManager() {
      return true;
    },
    isHR() {
      return true;
    },
  },
  created() {
    if (!this.isLoading) {
      this.init();
    }
  },
  updated() {},
  watch: {
    isLoading(val) {
      if (!val) {
        this.init();
      }
    },
    docsListUpdated(val) {
      if (val && !this.isLoading) {
        this.runSearch();
      }
    },
    "definition.docListColumns": {
      deep: true,
      handler() {
        this.setColumns();
      },
    },
    "definition.statuses": {
      deep: true,
      handler() {
        this.runSearch();
      },
    },
    filters() {
      if (this.initialised)
        this.runSearch();
    },
    sortColumn() {
      if (this.useIndex && this.initialised) {
        this.runSearch(true);
      }
    },
    sortDesc() {
      if (this.useIndex && this.initialised) {
        this.runSearch(true);
      }
    },
    currentPage() {
      if (this.useIndex && this.initialised) {
        this.runSearch(true);
      }
    },
    itemsPerPage() {
      if (this.useIndex && this.initialised) {
        this.runSearch(true);
      }
    },
  },
  methods: {
    setColumns() {
      if (
        this.definition.docListColumns &&
        this.definition.docListColumns.length
      ) {
        this.dtColumns = this.definition.docListColumns.map((c) => {
          if (['job_created_at', 'job_updated_at', 'status_date'].includes(c.value))
            c.sort = (a, b) =>
              moment(a, "D MMM YYYY").isAfter(moment(b, "D MMM YYYY"))
                ? 1
                : moment(a, "D MMM YYYY").isBefore(moment(b, "D MMM YYYY"))
                ? -1
                : 0;
          return c;
        });

        this.dtColumnsEnabled = this.definition.docListColumns
          .filter((c) => c.visible)
          .map((c) => c.value);

        if (
          this.definition.showEvaluationStatus ||
          this.definition.showEvaluationScore
        ) {
          let questionnaireStatusColumns = [];
          this.questionnaires.forEach((r) => {
            if (this.definition.showEvaluationStatus) {
              questionnaireStatusColumns.push({
                title: `${r.title} Status`,
                name: r.title,
                listcolumn: `docQuestionnaireStatus_${r.questionnaire_id}`,
                column: `docQuestionnaireStatus`,
                listcolumnColour: `docQuestionnaireStatusColour_${r.questionnaire_id}`,
                columnColour: `docQuestionnaireStatusColour`,
                questionnaire_id: r.questionnaire_id,
              });
              questionnaireStatusColumns.push({
                title: `${r.title} Progress`,
                name: r.title,
                listcolumn: `questionnaireStatuses_${r.questionnaire_id}`,
                column: `questionnaireStatuses`,
                questionnaire_id: r.questionnaire_id,
              });
            }
            if (this.definition.showEvaluationScore) {
              questionnaireStatusColumns.push({
                title: `${r.title} Score`,
                name: r.title,
                listcolumn: `docQuestionnaireScore_${r.questionnaire_id}`,
                column: `docQuestionnaireScore`,
                questionnaire_id: r.questionnaire_id,
              });
            }
          });

          questionnaireStatusColumns.forEach((c) => {
            this.dtColumns.push({
              text: c.title,
              value: c.column,
              source: c.listcolumn,
              sourceColour: c.listcolumnColour,
              questionnaire_id: c.questionnaire_id,
            });
            this.dtColumnsEnabled.push(c.column);
          });

          this.questionnaireStatusColumns = questionnaireStatusColumns;
        }
      }
    },
    async init() {
      this.docTypeIncluded = this.definitionDocTypes;
      this.hierarchies = this.$store.state.hierarchies.hierarchies.map((h) => {
        switch (h.label.toLowerCase()) {
          case "geography":
          case "location":
            h.icon = "mdi-map-marker-outline";
            break;
          default:
            h.icon = "mdi-menu-down";
            break;
        }
        h.treePickerValue = null;
        return h;
      });
      this.useTreePicker = !!this.$loginState.user.settings.find(
        (s) => s.setting === "hierarchy_tree_picker" && s.value === "true"
      );
      if (this.definition.showPieChart || this.definition.showCounts) {
        this.reportingStats.push({
          title: "Document Status",
          level: "document",
          column: "statusDisplayText",
          showPieChart: this.definition.showPieChart,
          showCounts: this.definition.showCounts,
          data: [],
          pieChartData: null,
          filter: null,
          show: true,
        });
      }
      if (
        this.definition.showEvaluationCounts ||
        this.definition.showEvaluationPieChart ||
        this.definition.showEvaluationStatus ||
        this.definition.showEvaluationScore
      ) {
        let docs = this.filters ? await this.$store.getters["docs/filter"](this.filters) : [];
        // this.questionnaires = [];
        // docs
        //   .filter((d) => d.questionnaires.length)
        //   .forEach((d) => {
        //     d.questionnaires
        //       .filter(
        //         (q) => !this.questionnaires.some((z) => z.name === q.name)
        //       )
        //       .forEach((q) =>
        //         this.questionnaires.push({
        //           name: q.name,
        //           id: q.id,
        //           questionnaire_id: q.questionnaire_id,
        //         })
        //       );
        //   });
        if (
          this.definition.showEvaluationCounts ||
          this.definition.showEvaluationPieChart
        ) {
          this.questionnaires.forEach((q) =>
            this.reportingStats.push({
              title: q.title,
              id: q.questionnaire_id,
              level: "questionnaire",
              groupColumn: "name",
              column: "docQuestionnaireStatus",
              showPieChart: this.definition.showEvaluationPieChart,
              showCounts: this.definition.showEvaluationCounts,
              data: [],
              pieChartData: null,
              filter: null,
              show: true,
            })
          );
        }
      }

      if (
        this.definition.showTimeInStatusFilter ||
        this.definition.docListColumns.some(c => c.value === 'tisTimeTosComplete' || c.value.startsWith('dis_'))
      ) {
        this.timeInStatusDataLoading = true;
        let possibleError = false;
        const tmplIds = this.docTypeIncluded.map((dt) => dt.tmpl_id);
        axios
            .get(`report/timeInStatus/${tmplIds.join(",")}`)
            .then(async (resp) => {
                if (resp.data.Status === "OK") {
                    timeInStatusData.clear();
                    resp.data.Data.forEach(d => {
                      timeInStatusData.set(d.doc_id, d);
                    });
                }

                this.timeInStatusDataLoading = false;
                this.setColumns();
                await this.runSearch();
                this.hasResults = this.applicationList.length > 0;
                
            })
            .catch((err) => {
                if (possibleError) {
                    alert("Code Error");
                } else if (err.response && err.response.status === 401) {
                    this.$emit("sessionExpired", err);
                } else {
                    alert(err.response ? err.response.data.message : err);
                }
                console.log(err);
                this.timeInStatusDataLoading = false;
            });
      } else {
        this.setColumns();
        await this.runSearch();
        this.hasResults = this.applicationList.length > 0;
      }
      
      this.initialised = true;
    },
    hryFilterChanged(hierarchyFilters, classifierFilters) {
      if (this.useIndex) {
        this.preSelectedHierarchyFilters = hierarchyFilters;
        this.preSelectedClassifierFilters = classifierFilters;
      } else {
        this.doHierarchyFilter(hierarchyFilters, classifierFilters);
      }
    },
    doHierarchyFilter(hierarchyFilters, classifierFilters) {
      if (hierarchyFilters)
        hierarchyFilters = JSON.parse(
          JSON.stringify(
            hierarchyFilters.map((f) => {
              return { ht_id: f.ht_id, selected: f.selected };
            })
          )
        );
      if (classifierFilters)
        classifierFilters = JSON.parse(
          JSON.stringify(
            classifierFilters.map((f) => {
              return { ct_id: f.ct_id, selected: f.selected };
            })
          )
        );

      let docs = [...this.applicationListFiltered];

      if (hierarchyFilters) {
        hierarchyFilters.forEach((hf) => {
          if (hf && hf.selected.length) {
            docs = docs.filter((d) =>
              hf.selected.find((s) => {
                let ht = d.hierarchies.find((ht) => ht.ht_id === hf.ht_id);
                return ht && s.id.split("_")[0] == ht.hr_id; // && !s.children
              })
            );
          }
        });
      }

      if (classifierFilters) {
        classifierFilters.forEach((cf) => {
          if (cf && cf.selected.length) {
            const cvs = cf.selected.map((c) => c.cv_id);
            docs = docs.filter(
              (d) =>
                d.classifiers.some((c) => cvs.includes(c.cv_id)) ||
                (cvs.includes(0) &&
                  !d.classifiers.some((c) => c.ct_id === cf.ct_id))
            );
          }
        });
      }

      this.preSelectedHierarchyFilters = hierarchyFilters;
      this.preSelectedClassifierFilters = classifierFilters;

      this.applicationListHryFiltered = Object.freeze(docs);
      this.totalItemCount = this.applicationListHryFiltered.length;

      this.setReportingStats();
    },
    clearDateFilter() {
      this.docCreatedDateRange.splice(0);
      this.docCreatedDateRangeMenu = false;
    },
    doDateFilter() {
      this.$refs.menu.save(this.docCreatedDateRange);
    },
    async doPagedFilter() {
      this.searchInProgress = true;
      const filterResult = await this.$store.getters["docs/pagedFilter"]({...this.filters,
        sortColumn: this.sortColumn,
        sortDesc: this.sortDesc,
        currentPage: this.currentPage,
        pageSize: this.itemsPerPage
      });
      this.totalItemCount = filterResult.totalCount;
      this.searchInProgress = false;
      return filterResult.documents;
    },
    async runSearch(skipStatsUpdate = false) {
      if (!this.filters) return;

      let applicationList = [];
      if (this.useIndex) {
        applicationList = await this.doPagedFilter();
      } else {
        applicationList = await this.$store.getters["docs/filter"](this.filters);      
      }
      
      applicationList.forEach((d) => {
        const requested = moment.utc(d.approval_requested_date);
        const ending = requested.add(90, "d");
        const remaining = ending.diff(moment(), "days");
        d.statusDisplayText = utils.resolveStatusChipText(d);
        d.ending = ending.format("DD MMM YYYY");
        d.endingInThirty =
          remaining > 0 && remaining < 30 && d.doc_status !== "APPROVED";
        if (remaining > 0 && d.doc_status !== "APPROVED")
          d.remainingTime = `${remaining} Days`;
        else d.remainingTime = "-";

        if (this.questionnaireStatusColumns.length) {
          d.questionnaires.forEach((q) => {
            this.questionnaireStatusColumns
              .filter((c) => c.name === q.name)
              .forEach((c) => {
                d[c.listcolumn] = q[c.column];
                if (c.listcolumnColour)
                  d[c.listcolumnColour] = q[c.columnColour];
              });
          });
        }

        const timeInStatus = timeInStatusData.get(Number(d.doc_id));
        if (timeInStatus) {
          for (const prop in timeInStatus) {
            d[prop] = timeInStatus[prop];
            if (prop.startsWith('tis_')) {
              d[prop.replace('tis_','dis_')] = Math.round(timeInStatus[prop] / (24 * 60));
            }
          }
        }
      });
      this.applicationList = applicationList;

      let applicationListFiltered = [...applicationList];

      if (this.useIndex) {
        this.applicationListFiltered = applicationListFiltered;
        this.applicationListHryFiltered = applicationListFiltered;
        if (!skipStatsUpdate)
          this.setReportingStatsIndex();
      } else {
        this.reportingStats
          .filter((x) => x.filter)
          .forEach((r) => {
            if (r.level === "document" && r.filter === this.endIn30)
              applicationListFiltered = applicationListFiltered.filter(
                (d) => d.endingInThirty
              );
            else if (r.level === "document")
              applicationListFiltered = applicationListFiltered.filter(
                (d) => d[r.column] === r.filter
              );
            else if (r.level === "questionnaire")
              applicationListFiltered = applicationListFiltered.filter((d) =>
                d.questionnaires.some(
                  (q) => q[r.groupColumn] === r.title && q[r.column] === r.filter
                )
              );
          });

        if (this.timeInStatusIncluded && this.timeInStatusIncluded.length) {
          this.timeInStatusIncluded.forEach(f => {
            applicationListFiltered = applicationListFiltered.filter(d => {
              const daysInStatus = d[`dis_${f.status}`] || 0;
              return daysInStatus >= f.min && daysInStatus <= f.max;
            })
          })
        }

        this.applicationListFiltered = applicationListFiltered;

        this.doHierarchyFilter(
          this.preSelectedHierarchyFilters,
          this.preSelectedClassifierFilters
        );      
      }
    },
    async setReportingStatsIndex() {
      if (this.reportingStats.length === 0) return;
      
      this.reportingStats.forEach((r) => {
        r.data = [];
        r.pieChartData = null;
      });
      const data = this.reportingStats
          .map(r => {
            if (r.column === 'statusDisplayText')
              return {
                tag: `doc_status`,
                filters: this.filters
              };
            else if (r.column === 'docQuestionnaireStatus')
              return {
                tag: `qs_${r.id}`,
                filters: this.filters,
              }
          })
          .filter(x => x);

      await this.$store.getters['docs/getCountsByAttr'](data)
        .then(resp => {
          resp.forEach(t => {
            let stat;
            if (t.tag === 'doc_status') {
              stat = this.reportingStats.find(r => r.column === 'statusDisplayText');
              if (stat) {
                stat.data = t.values.map(c => {
                  const statusSummary = this.docStatusSummary.find((s) => s.status === c.value);
                  return {
                    status: c.value,
                    colour: statusSummary?.colour || '',
                    count: c.count,
                    position: statusSummary?.position || 99
                  };
                });
                stat.data.sort((a, b) => {
                  if (a.position === b.position)
                    return a.count < b.count ? 1 : -1;
                  else
                    return a.position > b.position ? 1 : -1;
                });
                if (stat.showPieChart)
                  stat.pieChartData = this.setPieChartData(stat.data);
                else
                  stat.pieChartData = null;
              }
            } else if (t.tag.startsWith('qs_')) {
              const qid = parseInt(t.tag.replace('qs_',''));
              stat = this.reportingStats.find(r => r.column === 'docQuestionnaireStatus' && r.id === qid);
              if (stat) {
                stat.data = t.values.map(c => {
                  return {
                    status: c.value,
                    colour: utils.getQuestionnaireStatusColour(c.value),
                    count: c.count
                  };
                });
                if (stat.showPieChart)
                  stat.pieChartData = this.setPieChartData(stat.data);
                else
                  stat.pieChartData = null;
              }
            }
          })
        });
    },
    setReportingStats() {
      this.reportingStats.forEach((r) => (r.data = []));
      this.applicationListHryFiltered.forEach(d => {
        this.reportingStats.forEach((r) => {
          if (r.level === "document") {
            let item = r.data.find((s) => s.status === d[r.column]);
            if (!item) {
              const statusSummary = this.docStatusSummary.find((s) => s.status === d[r.column]);
              item = {
                status: d[r.column],
                colour: utils.resolveHeaderStyle(d),
                count: 0,
                position: statusSummary?.position || 99
              };
              r.data.push(item);
            }
            item.count++;
            if (
              this.definition.docTypes.length === 1 &&
              this.definition.docTypes[0].toLowerCase() === "flex apply" &&
              d.endingInThirty
            ) {
              item = r.data.find((s) => s.status === this.endIn30);
              if (!item) {
                item = {
                  status: this.endIn30,
                  colour: "",
                  count: 0,
                };
                r.data.push(item);
              }
              item.count++;
            }
          } else if (r.level === "questionnaire") {
            let q = d.questionnaires.find((x) => x[r.groupColumn] === r.title);
            if (q) {
              let item = r.data.find((s) => s.status === q[r.column]);
              if (!item) {
                item = {
                  status: q[r.column],
                  colour: q[r.column + "Colour"],
                  count: 0,
                };
                r.data.push(item);
              }
              item.count++;
            }
          }
        });
      });

      this.reportingStats
        .filter((x) => x.showPieChart)
        .forEach((r) => (r.pieChartData = this.setPieChartData(r.data)));

      this.reportingStats
        .filter(r => r.level === 'document')
        .forEach(r => {
          r.data.sort((a, b) => {
            if (a.position === b.position)
              return a.count < b.count ? 1 : -1;
            else
              return a.position > b.position ? 1 : -1;
          });
        });
    },
    setPieChartData(data) {
      return data.map(s => {
        return {
          label: s.status,
          value: s.count,
          colour: s.colour 
        }
      });
    },
    getDocTypeFilterText(item, index) {
      if (item && index > 0) return "";
      let included = this.definitionDocTypes.filter((s) =>
        this.docTypeIncluded.some((si) => s.docType === si.docType)
      );
      if (included.length === this.definitionDocTypes.length)
        return included.length > 0 ? "All" : "None";

      if (included.length > 2) return `${included.length} items`;

      return included.reduce((prev, curr) => {
        return prev + (prev ? ", " : "") + curr.docType;
      }, "");
    },
    pieRowSelected(stat, value) {
      if (value) {
        const status = stat.find((c) => c.status === value);
        if (status) this.toggleStatusFilter(stat, status);
      }
    },
    toggleStatusFilter(stat, status) {
      if (stat.filter && stat.filter === status.status) stat.filter = null;
      else stat.filter = status.status;

      this.reportingStats
        .filter((x) => x.title !== stat.title)
        .forEach((r) => (r.filter = null));
    },
    openDocument(item) {
      this.doc_id_opened = item.doc_id;
      this.$emit("openDocument", this.docDetails(item));
    },
    docDetails(item) {
      return {
        doc_id: item.doc_id,
        tmpl_id: item.tmpl_id,
        system_number: utils.removeTags(item.system_number),
        doc_name: utils.removeTags(item.doc_name),
        doc_type: item.doc_type,
        viewAction: item.viewAction,
      };
    },
    searchText($event, hrchy) {
      let ctl = this.$refs["hcb" + this.focusHTID][0];
      if ([13, 37, 38, 39, 40].indexOf($event.keyCode) >= 0) return;
      if (ctl.lazySearch) {
        if ($event.keyCode !== 13) hrchy.searchText = ctl.lazySearch.trim();
        let match = ctl.filteredItems.find(
          (f) => f.text.toLowerCase() === ctl.lazySearch.toLowerCase()
        );
        if (match) {
          ctl.selectItem(match);
          hrchy.hierarchy_text = match.text;
          hrchy.hr_id = match.hr_id;
          hrchy.showNewPrompt = false;
        } else {
          hrchy.showNewPrompt =
            ctl.filteredItems.length &&
            (!hrchy.hierarchy_text ||
              hrchy.hierarchy_text.toLowerCase() !==
                hrchy.searchText.toLowerCase());
        }
      } else {
        hrchy.hierarchy_text = "";
        hrchy.showNewPrompt = false;
      }
    },
    showMeasure(settings, measure) {
      let item = settings.activeMeasures.findIndex((m) => m === measure.name);
      if (item >= 0) settings.activeMeasures.splice(item, 1);
      else settings.activeMeasures.push(measure.name);
    },
    exportToCSV() {
      let csvHeaders = [];
      this.dtColumns.forEach((c) => {
        if (c.value === "docQuestionnaireStatus") {
          csvHeaders.push({ text: c.text, value: c.value, source: c.source, questionnaire_id: c.questionnaire_id });
        } else if (c.value === "questionnaireStatuses") {
          const statuses = [];
          this.applicationListFiltered.forEach((d) => {
            const source = d[c.source];
            if (source && source.length && source[0].status !== "Unassigned") {
              source.forEach((rs) => {
                if (!statuses.includes(rs.status)) statuses.push(rs.status);
              });
            }
          });
          statuses.forEach((s) => {
            csvHeaders.push({
              text: `${c.text} ${s}`,
              value: "questionnaireStatuses",
              source: c.source,
              status: s,
              questionnaire_id: c.questionnaire_id,
            });
          });
        } else if (
          c.value === "doc_status_text" &&
          this.applicationListFiltered.some(
            (x) =>
              x.lifecycle_status_non_responders || x.lifecycle_status_responders
          )
        ) {
          csvHeaders.push(c);
          csvHeaders.push({
            text: `Waiting For`,
            value: "lifecycle_status_non_responders",
          });
          csvHeaders.push({
            text: `Completed By`,
            value: "lifecycle_status_responders",
          });
        } else {
          csvHeaders.push(c);
        }
      });

      let hasTimeInStatus = csvHeaders.some(c => c.value === 'tisTimeToComplete');
      if (hasTimeInStatus) {
        csvHeaders = csvHeaders.filter(c => c.value !== 'tisTimeToComplete' && !c.value.startsWith('dis_'));
        csvHeaders.push(...this.docStatuses
          .filter(s => !s.status.startsWith('Part '))
          .map(s => {
            return {
              text: `Days in ${s.status}`,
              value: 'dis_' + s.status,
            }
          }))
      }

      const fileName = `${this.$loginState.user.client.replace(
          new RegExp("\\s+", "g"),
          "_"
        )}_${this.$route.name.replace(
          new RegExp("\\s+", "g"),
          "_"
        )}_${moment().format("YYYYMMDDhhmmss")}.csv`;

      if (this.useIndex) {
        const params = {
          filters: JSON.stringify(this.filters),
          includeColumns: JSON.stringify(csvHeaders),
          hasTimeInStatus: hasTimeInStatus,
          fileName: fileName,
          authToken: this.$loginState.rmapiAccessToken,
        };
        this.formPost(
          process.env.VUE_APP_API_BASE_URL + "document/docListCsv",
          params
        );
      } else {
        this.exportToCSVLocal(csvHeaders, fileName);
      }
    },
    exportToCSVLocal(csvHeaders, fileName) {
      let data = csvHeaders.map((h) => '"' + h.text + '"').join(",") + "\n";

      this.applicationListFiltered.forEach((d) => {
        data +=
          csvHeaders
            .map((h) => {
              let raw = null;
              if (h.value === "questionnaireStatuses") {
                const source = d[h.source];
                if (
                  source &&
                  source.length &&
                  source[0].status !== "Unassigned"
                ) {
                  raw = source.find((s) => s.status === h.status)?.count || 0;
                }
              } else if (["docQuestionnaireScore", "docQuestionnaireStatus"].includes(h.value)) {
                raw = d[h.source];
              } else {
                raw = d[h.value];
              }
              return utils.csvEscape(utils.removeTags(raw));
            })
            .join(",") + "\n";
      });

      utils.downloadFile(data, fileName, "text/csv;encoding:utf-8");
    },
    formPost(path, params) {
      const frameName = `formPostFrame_${Date.now()}`;
      const iframe = document.createElement("iframe");
      iframe.name = frameName;
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      const form = document.createElement("form");
      form.method = "POST";
      form.action = path;
      form.target = frameName;

      let key;
      for (key in params) {
        if (Object.hasOwn(params, key)) {
          const hiddenField = document.createElement("input");
          hiddenField.type = "hidden";
          hiddenField.name = key;
          hiddenField.value = params[key];
          form.appendChild(hiddenField);
        }
      }

      document.body.appendChild(form);
      
      // Remove the iframe once the response has been delivered so the DOM doesn't accumulate frames.
      iframe.addEventListener("load", () => {
        document.body.removeChild(form);
        document.body.removeChild(iframe);
      });

      form.submit();
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/vars";

.flex-container {
  display: flex;

  .left-col {
    z-index: 3;
    flex: 0 0 400px;
    width: 400px !important;
    height: calc(100vh - 48px) !important;
    position: fixed;
    top: 48px !important;
    left: var(--side-menu-width, 0px);
    overflow: visible !important;
  }

  .right-col {
    padding-top: 0;
    padding-right: 5px;
    transition: padding 200ms;
    &.hierarchiesPinned {
      padding-left: 370px;
    }
  }

  @media (max-width: 600px) {
    .right-col {
      padding-left: 0;
    }
  }
}

.hierarchies-collapsed {
  min-height: calc(100vh - 103px) !important;
  .showHierarchies {
    position: fixed;
    top: 82px;
    left: calc(var(--side-menu-width, 0px) + 5px);
  }
}

.showHierarchies {
  top: 32px;
  right: -15px;
}

.pieContainer {
  max-width: 150px;
  margin: auto;
}

.statusCounts {
  width: 100%;
  min-width: none;
  display: flex;
  justify-content: flex-start;
  padding: 5px;

  > div {
    display: flex;
    margin: 0 5px;
    min-width: 200px;
    max-width: 15%;
    cursor: pointer;
    border-radius: 5px;
    &:hover,
    &.active {
      background-color: rgba($color: #cccccc, $alpha: 0.2);
    }
  }
}

::v-deep .v-data-table {
  tbody > tr,
  tbody > tr span {
    cursor: pointer;
  }
}

@media (max-width: 600px) {
  .footer-actions {
    padding-left: 15px;
  }
}

.footer-actions {
  width: calc(100% - var(--side-menu-width, 0px));
  position: fixed;
  bottom: 0;
  left: var(--side-menu-width, 0px);
  right: 0;
  z-index: 4;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.footer-actions {
  display: flex;
  align-items: center;
  padding: 0 15px;
  height: 55px;

  div {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
  }
}
</style>